import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import WarehouseAddress from './WarehouseAddress';
import Instruction, { InstructionList } from './Instruction';
import s from './ReturnInstructions.module.css';
import {
  BOXLESS,
  MAIL,
  NARVAR_CONCIERGE,
  CONCIERGE_PARTNERSHIP,
  PRINTERLESS_MAIL,
  PICKUP,
  HAPPY_RETURNS,
  HOME_PICKUP,
  LOCKER_DROPOFF,
  PRINTERLESS_METHODS,
} from '../../constants/returnMethods';
import DEFAULT_COPY from '../../constants/defaultCopy';
import { AppCta } from '../../components/AppCta';
import { getWarehouseAddress } from '../../controllers/Step4/helpers';
import { returnItemsService } from '../../services';
import { store } from '../../store';
import { logData } from '../../actions/analytics';
import { EVENT_VALUES, EVENTS } from '../../constants/noflake/noflakeSchema';
import { Language } from '../Language';

const mapStateToProps = ({
  returnMethods: { selectedMethodKey } = {},
  storeLocator: { selectedLocation } = {},
  page: { productType, secureMode, languageCode } = {},
  returnsSettings: { returns: { returnOptions } = {} } = {},
  returnSubmission: {
    printerlessUrl,
    labelUrl,
    returnProcessInfo: {
      partner_return_code: partnerReturnCode,
      is_label_data_present: showPrintLabelButton,
      is_qrcode_present: showQrCode,
    } = {},
  } = {},
  pickup: { form: { date: pickupDate, pickupTime } = {} } = {},
}) => ({
  selectedMethodKey,
  selectedLocation,
  productType,
  returnOptionsEnabled: returnOptions && returnOptions.enabled,
  isButtonEnabled: !secureMode,
  languageCode,
  partnerReturnCode,
  pickupDate,
  pickupTime,
  showPrintLabelButton,
  showQrCode,
  printerlessUrl,
  labelUrl,
});

const ReturnInstructions = ({
  selectedMethodKey,
  selectedLocation,
  productType,
  returnOptionsEnabled,
  isButtonEnabled,
  languageCode,
  partnerReturnCode,
  pickupDate,
  pickupTime,
  showPrintLabelButton,
  showQrCode,
  printerlessUrl,
  labelUrl,
}) => {
  const { [languageCode]: DEFAULT_LANG_COPY = {} } = DEFAULT_COPY || {};

  const warehouseAddress = getWarehouseAddress();
  const returnMethod = returnItemsService.getMethod();

  const onClick = () => {
    store.dispatch(
      logData({
        type: EVENTS.CLICK,
        value: EVENT_VALUES[EVENTS.CLICK].confirmation_page_cta_button,
      }),
    );
    const url =
      showQrCode || PRINTERLESS_METHODS.includes(returnItemsService.getMethod())
        ? printerlessUrl
        : labelUrl;
    window.open(url, '_blank');
  };

  const renderInstructions = () => {
    const props = {
      defaultLanguage: DEFAULT_LANG_COPY,
      methodKey: selectedMethodKey,
      returnMethod,
    };
    if (productType === 'repairs') {
      return (
        <InstructionList>
          <Instruction {...props} number={1} />
          <Instruction {...props} number={2} />
          <Instruction {...props} number={3} location={selectedLocation} />
          <WarehouseAddress address={warehouseAddress} />
        </InstructionList>
      );
    }
    if (returnMethod === MAIL || returnMethod === NARVAR_CONCIERGE) {
      return (
        <InstructionList>
          <Instruction {...props} number={1} button={isButtonEnabled} onClick={onClick} />
          <Instruction {...props} number={2} />
          <Instruction {...props} number={3} location={selectedLocation} />
        </InstructionList>
      );
    }
    if (returnMethod === PICKUP) {
      if (showQrCode) {
        return (
          <InstructionList>
            <Instruction {...props} number={1} />
            <Instruction {...props} number={2} button={isButtonEnabled} onClick={onClick} />
          </InstructionList>
        );
      }
      if (showPrintLabelButton) {
        return (
          <InstructionList>
            <Instruction {...props} number={1} button={isButtonEnabled} onClick={onClick} />
            <Instruction {...props} number={2} />
            <Instruction {...props} number={3} location={selectedLocation} />
          </InstructionList>
        );
      }
      return (
        <InstructionList>
          <Instruction {...props} number={1} />
          <Instruction {...props} number={2} />
        </InstructionList>
      );
    }
    if (returnOptionsEnabled && returnMethod === PRINTERLESS_MAIL) {
      return (
        <InstructionList>
          <Instruction {...props} number={1} />
          <Instruction {...props} number={2} />
          {!isEmpty(selectedLocation) && !isEmpty(selectedLocation.store_address) && (
            <Instruction {...props} number={2} displayBullet={false} location={selectedLocation} />
          )}
          <Instruction {...props} number={3} button={isButtonEnabled} onClick={onClick} />
        </InstructionList>
      );
    }
    if (returnMethod === BOXLESS || returnMethod === HAPPY_RETURNS) {
      return (
        <InstructionList>
          <Instruction {...props} number={1} />
          <Instruction {...props} number={1} displayBullet={false} location={selectedLocation} />
          <Instruction {...props} number={2} button={isButtonEnabled} onClick={onClick} />
        </InstructionList>
      );
    }
    if (returnMethod === CONCIERGE_PARTNERSHIP) {
      return (
        <InstructionList>
          <Instruction {...props} number={1} />
          <Instruction {...props} number={2} location={selectedLocation} />
          <Instruction {...props} number={3} partnerReturnCode={partnerReturnCode} />
        </InstructionList>
      );
    }
    if (returnMethod === PRINTERLESS_MAIL) {
      return (
        <InstructionList>
          <Instruction {...props} number={1} button={isButtonEnabled} onClick={onClick} />
          <Instruction {...props} number={2} location={selectedLocation} />
        </InstructionList>
      );
    }
    if (returnMethod === 'store') {
      return (
        <InstructionList>
          <Instruction {...props} number={1} button={isButtonEnabled} onClick={onClick} />
          <Instruction {...props} number={2} storeLink />
          {!isEmpty(selectedLocation) && (
            <Instruction {...props} number={3} displayBullet={false} location={selectedLocation} />
          )}
        </InstructionList>
      );
    }
    if (returnMethod === 'self') {
      return (
        <InstructionList>
          <Instruction {...props} number={1} button={isButtonEnabled} onClick={onClick} />
          <Instruction {...props} number={2} />
          <Instruction {...props} number={3} />
          <WarehouseAddress address={warehouseAddress} />
        </InstructionList>
      );
    }
    if (returnMethod === HOME_PICKUP) {
      return (
        <InstructionList>
          <Instruction {...props} number={1} button={isButtonEnabled} onClick={onClick} />
          <Instruction {...props} number={2} />
          <Instruction {...props} number={3} pickupDate={pickupDate} pickupTime={pickupTime} />
        </InstructionList>
      );
    }
    if (returnMethod === LOCKER_DROPOFF) {
      return (
        <InstructionList>
          <Instruction {...props} number={1} />
          <Instruction {...props} number={1} displayBullet={false} location={selectedLocation} />
          <Instruction {...props} number={2} button={isButtonEnabled} onClick={onClick} />
        </InstructionList>
      );
    }
    return null;
  };

  return (
    <div>
      <AppCta />
      <Language
        tagName="p"
        langKey={[
          `returns.step_4.return_instructions_header.${selectedMethodKey}`,
          'returns.step_4.return_instructions_header',
        ]}
        className={cx('return-instructions-header', s.header)}
      />
      {renderInstructions()}
    </div>
  );
};

ReturnInstructions.propTypes = {
  selectedMethodKey: PropTypes.string,
  productType: PropTypes.string,
  returnOptionsEnabled: PropTypes.bool,
  returnMethod: PropTypes.string,
  onClick: PropTypes.func,
  selectedLocation: PropTypes.object,
  isButtonEnabled: PropTypes.bool,
  languageCode: PropTypes.string,
  partnerReturnCode: PropTypes.string,
  pickupDate: PropTypes.string,
  pickupTime: PropTypes.string,
  showPrintLabelButton: PropTypes.bool,
  showQrCode: PropTypes.bool,
  printerlessUrl: PropTypes.string,
  labelUrl: PropTypes.string,
};

ReturnInstructions.defaultProps = {
  selectedLocation: {},
  partnerReturnCode: null,
  pickupDate: '',
  pickupTime: '',
  showQrCode: false,
  printerlessUrl: '',
  labelUrl: '',
  selectedMethodKey: null,
  productType: null,
  returnOptionsEnabled: false,
  returnMethod: null,
  onClick: () => {},
  isButtonEnabled: false,
  languageCode: null,
  showPrintLabelButton: false,
};

export default connect(mapStateToProps)(ReturnInstructions);
export { mapStateToProps, ReturnInstructions };
